import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Loader2, RotateCcw, AlertTriangle, Boxes, KeyRound, Lock, Info } from 'lucide-react';
import { PageHeader, Button } from '../ui-v2';
import { Input, Select, Switch } from '../ui-v2.5';
import IconPicker from '../soporte/components/IconPicker';
import {
  getRecurso, crearRecurso, actualizarRecurso, listAreas, listTiposActivo, listRecursos,
} from './api';
import EditorInstrucciones from './components/EditorInstrucciones';
import { CAMPOS_PERSONA, CAMPOS_PASSWORD } from './camposPersona';

/**
 * RecursoForm — alta y edición de un recurso del catálogo, en PÁGINA PROPIA.
 *
 * POR QUÉ PÁGINA Y NO MODAL, que es lo que usan las otras cuatro pantallas de configuración:
 * con el tipo, la columna de identidad, la de contraseña, las instrucciones con editor rico, el
 * área, el orden y lo inventariable, el formulario dejó de caber en un modal. Las otras cuatro
 * siguen con modal porque siguen siendo cuatro campos; la asimetría está justificada por el
 * tamaño, no por capricho.
 *
 * Los dos modales de CONFIRMACIÓN —desactivar y eliminar— se quedan donde estaban: son
 * confirmaciones, no formularios.
 *
 * DOS COLUMNAS: a la izquierda lo que define el recurso pase lo que pase; a la derecha lo que
 * depende de su tipo. El editor ocupa el alto que necesita sin empujar al resto.
 */

// Para frases: «el material de colaboradoras», no «de colaborador».
const ambitoFrase = (scope) => (scope === 'colab' ? 'colaboradoras' : 'personal interno');
const cuerpoError = (e) => (e && e.response && e.response.data) || {};

// Las dos listas viven en `camposPersona.js`: la pantalla del alta usa la misma.

const RUTA_LISTA = '/gestionpersonas/personas/incorporaciones/recursos';

export default function RecursoForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const esEdicion = !!id;

  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [areas, setAreas] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [recursos, setRecursos] = useState([]);
  const [reactivable, setReactivable] = useState(null);
  const [errores, setErrores] = useState({});

  const [nombre, setNombre] = useState('');
  const [icono, setIcono] = useState('');
  const [areaId, setAreaId] = useState('');
  const [tipoRecurso, setTipoRecurso] = useState('material');
  const [inventariable, setInventariable] = useState(false);
  const [tipoId, setTipoId] = useState('');
  // El tipo de Inventario de COLABORADORAS (15/10). Un recurso, un área, un tipo por ámbito.
  const [tipoColabId, setTipoColabId] = useState('');
  const [campoPersona, setCampoPersona] = useState('');
  const [campoPassword, setCampoPassword] = useState('');
  const [instrucciones, setInstrucciones] = useState('');
  const [porDefecto, setPorDefecto] = useState(true);
  const [orden, setOrden] = useState(0);

  // Instantánea del formulario recién cargado. Es la referencia contra la que se decide si hay
  // cambios sin guardar: comparar contra "vacío" daría falsos positivos en edición.
  const inicial = useRef(null);
  const guardadoRef = useRef(false);

  const estadoActual = useMemo(() => JSON.stringify({
    nombre, icono, areaId: String(areaId), tipoRecurso, inventariable, tipoId: String(tipoId), tipoColabId: String(tipoColabId),
    campoPersona, campoPassword, instrucciones, porDefecto, orden: String(orden),
  }), [nombre, icono, areaId, tipoRecurso, inventariable, tipoId, tipoColabId, campoPersona, campoPassword, instrucciones, porDefecto, orden]);

  const hayCambios = inicial.current !== null && inicial.current !== estadoActual && !guardadoRef.current;

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(false);
    try {
      const [ars, tps, recs, rec] = await Promise.all([
        listAreas(), listTiposActivo(), listRecursos(),
        esEdicion ? getRecurso(id) : Promise.resolve(null),
      ]);
      setAreas(ars);
      setTipos(tps?.tipos || []);
      setRecursos(recs);
      if (rec) {
        setNombre(rec.nombre || '');
        setIcono(rec.icono || '');
        setAreaId(rec.area_id ? String(rec.area_id) : '');
        setTipoRecurso(rec.tipo || 'material');
        setInventariable(!!rec.inventariable);
        setTipoId(rec.inv_asset_type_id ? String(rec.inv_asset_type_id) : '');
        setTipoColabId(rec.inv_asset_type_colab_id ? String(rec.inv_asset_type_colab_id) : '');
        setCampoPersona(rec.campo_persona || '');
        setCampoPassword(rec.campo_password || '');
        setInstrucciones(rec.instrucciones_acceso || '');
        setPorDefecto(!!rec.por_defecto);
        setOrden(rec.orden ?? 0);
      }
    } catch (e) {
      console.error('[incorp][recurso-form] cargar', e);
      setError(true);
    } finally {
      setCargando(false);
    }
  }, [id, esEdicion]);

  useEffect(() => { cargar(); }, [cargar]);

  // Se toma la referencia DESPUÉS de pintar con los datos cargados, no dentro de `cargar`:
  // así incluye lo que los estados hayan normalizado.
  useEffect(() => {
    if (!cargando && inicial.current === null) inicial.current = estadoActual;
  }, [cargando, estadoActual]);

  // Aviso al cerrar la pestaña o recargar. El de la navegación interna va en `salir()`:
  // react-router v6 no expone bloqueo de navegación sin data router, así que se controla en
  // los puntos de salida que sí son nuestros.
  useEffect(() => {
    const alSalir = (e) => {
      if (!hayCambios) return undefined;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', alSalir);
    return () => window.removeEventListener('beforeunload', alSalir);
  }, [hayCambios]);

  const esCredencial = tipoRecurso === 'credencial';
  const areasActivas = useMemo(() => areas.filter((a) => a.activa), [areas]);

  const opcionesArea = useMemo(() => {
    const base = areasActivas.map((a) => ({ id: a.id, nombre: a.nombre }));
    const actual = recursos.find((r) => String(r.id) === String(id));
    if (esEdicion && actual && actual.area && !base.some((a) => a.id === actual.area.id)) {
      return [{ id: actual.area.id, nombre: `${actual.area.nombre} (inactiva)` }, ...base];
    }
    return base;
  }, [areasActivas, esEdicion, recursos, id]);

  // Cada desplegable, sólo con los tipos de SU ámbito: el servidor rechaza cruzarlos, y ofrecerlos
  // sería invitar al error.
  const tiposDe = useMemo(() => ({
    interno: tipos.filter((x) => x.scope === 'interno'),
    colab: tipos.filter((x) => x.scope === 'colab'),
  }), [tipos]);

  // Quién reclama ya cada columna. Espejo en cliente de los dos UNIQUE de BBDD.
  const ocupadas = useMemo(() => {
    const m = { campo_persona: new Map(), campo_password: new Map() };
    for (const r of recursos) {
      if (esEdicion && String(r.id) === String(id)) continue;
      if (r.campo_persona) m.campo_persona.set(r.campo_persona, r.nombre);
      if (r.campo_password) m.campo_password.set(r.campo_password, r.nombre);
    }
    return m;
  }, [recursos, esEdicion, id]);

  function cambiarTipo(v) {
    setTipoRecurso(v);
    setErrores({});
    if (v === 'credencial') { setInventariable(false); setTipoId(''); setTipoColabId(''); }
    else { setCampoPersona(''); setCampoPassword(''); setInstrucciones(''); }
  }

  function toggleInventariable(v) {
    setInventariable(v);
    if (!v) { setTipoId(''); setTipoColabId(''); setErrores((e) => ({ ...e, tipo: undefined })); }
  }

  function validar() {
    const err = {};
    const n = nombre.trim();
    if (!n) err.nombre = 'El nombre es obligatorio.';
    else if (n.length > 80) err.nombre = 'Máximo 80 caracteres.';
    if (!icono) err.icono = 'Elige un icono.';
    if (!areaId) err.area = 'Selecciona un área responsable.';
    if (inventariable && !tipoId && !tipoColabId) err.tipo = 'Un recurso inventariable necesita un tipo de Inventario, de internos, de colaboradoras o de los dos.';
    if (esCredencial && campoPersona && ocupadas.campo_persona.has(campoPersona)) {
      err.campoPersona = `Ya lo reclama «${ocupadas.campo_persona.get(campoPersona)}». Cada columna pertenece a un solo recurso.`;
    }
    if (esCredencial && campoPassword && ocupadas.campo_password.has(campoPassword)) {
      err.campoPassword = `Ya lo reclama «${ocupadas.campo_password.get(campoPassword)}». Cada columna pertenece a un solo recurso.`;
    }
    const o = Number(orden);
    if (!Number.isInteger(o) || o < 0) { toast.error('El orden debe ser un entero ≥ 0.'); return null; }
    setErrores(err);
    if (Object.keys(err).length) return null;
    return {
      nombre: n, icono, inventariable,
      inv_asset_type_id: inventariable && tipoId ? Number(tipoId) : null,
      inv_asset_type_colab_id: inventariable && tipoColabId ? Number(tipoColabId) : null,
      area_id: Number(areaId), por_defecto: porDefecto, orden: o,
      tipo: tipoRecurso,
      campo_persona: esCredencial ? (campoPersona || null) : null,
      campo_password: esCredencial ? (campoPassword || null) : null,
      instrucciones_acceso: esCredencial ? (instrucciones || null) : null,
    };
  }

  async function guardar() {
    const payload = validar();
    if (!payload) return;
    setGuardando(true);
    setReactivable(null);
    try {
      const r = esEdicion ? await actualizarRecurso(id, payload) : await crearRecurso(payload);
      // El servidor dice si descartó imágenes. Callarlo dejaría a quien escribió el correo
      // creyendo que la miniatura está, y solo se enteraría el destinatario.
      if (r && r.aviso_imagenes_descartadas && r.aviso_imagenes_descartadas.length) {
        toast.warn(`Se descartaron ${r.aviso_imagenes_descartadas.length} imagen(es) de origen no permitido. Solo se admiten miniaturas de Vimeo.`);
      }
      guardadoRef.current = true;
      toast.success(esEdicion ? 'Recurso actualizado.' : 'Recurso creado.');
      navigate(RUTA_LISTA);
    } catch (e) {
      const body = cuerpoError(e);
      if (body.error === 'nombre_duplicado_inactivo' && body.id) setReactivable({ id: body.id, nombre: payload.nombre });
      else if (body.error === 'nombre_duplicado') setErrores((p) => ({ ...p, nombre: 'Ya existe un recurso con ese nombre.' }));
      else if (body.error === 'campo_persona_ocupado') setErrores((p) => ({ ...p, campoPersona: body.message }));
      else if (body.error === 'campo_password_ocupado') setErrores((p) => ({ ...p, campoPassword: body.message }));
      else {
        console.error('[incorp][recurso-form] guardar', e);
        toast.error(body.message || 'No se pudo guardar el recurso.');
      }
    } finally {
      setGuardando(false);
    }
  }

  async function reactivarDuplicado() {
    if (!reactivable) return;
    const payload = validar();
    if (!payload) return;
    setGuardando(true);
    try {
      await actualizarRecurso(reactivable.id, { ...payload, activo: true });
      guardadoRef.current = true;
      toast.success(`Recurso "${reactivable.nombre}" reactivado.`);
      navigate(RUTA_LISTA);
    } catch (e) {
      console.error('[incorp][recurso-form] reactivarDuplicado', e);
      toast.error(cuerpoError(e).message || 'No se pudo reactivar el recurso.');
    } finally {
      setGuardando(false);
    }
  }

  function salir() {
    if (hayCambios && !window.confirm('Hay cambios sin guardar. ¿Seguro que quieres salir y perderlos?')) return;
    navigate(RUTA_LISTA);
  }

  if (cargando) {
    return <div className="flex items-center justify-center p-12 text-v25-ink-400"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }
  if (error) {
    return (
      <div className="p-6">
        <p className="text-sm text-red-600">No se pudo cargar el recurso.</p>
        <Button variant="outline" className="mt-3" onClick={() => navigate(RUTA_LISTA)}><ArrowLeft />Volver</Button>
      </div>
    );
  }

  return (
    <div className="p-6 pb-24">
      <PageHeader
        title={esEdicion ? 'Editar recurso' : 'Nuevo recurso'}
        subtitle={esEdicion ? nombre : 'Define el recurso, su tipo y su área responsable.'}
        action={<Button variant="outline" onClick={salir}><ArrowLeft />Volver al catálogo</Button>}
      />

      <div className="mt-4 grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* ── Izquierda · lo que define al recurso, sea del tipo que sea ── */}
        <div className="space-y-4">
          <Input label="Nombre *" value={nombre} onChange={(e) => setNombre(e.target.value)}
            error={errores.nombre} maxLength={80} autoFocus />

          <div>
            <label className="mb-1 block text-xs font-medium text-v25-ink-700">Icono *</label>
            <IconPicker value={icono} onChange={setIcono} disabled={guardando} />
            {errores.icono && <p className="mt-1 text-[11px] text-red-600">{errores.icono}</p>}
          </div>

          <Select label="Tipo de recurso *" value={tipoRecurso} onChange={(e) => cambiarTipo(e.target.value)}
            disabled={guardando}
            hint={esCredencial
              ? 'Una cuenta o un acceso. Se marca cuando Sistemas la crea, y la baja la revoca.'
              : 'Algo que se entrega o se prepara: un portátil, una camiseta, una tarjeta.'}>
            <option value="material">Material</option>
            <option value="credencial">Credencial</option>
          </Select>

          <Select label="Área responsable *" value={areaId} onChange={(e) => setAreaId(e.target.value)} error={errores.area}>
            <option value="">Selecciona un área…</option>
            {opcionesArea.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
          </Select>

          <div className="flex items-center justify-between gap-4">
            <Switch checked={porDefecto} onChange={setPorDefecto} disabled={guardando} label="Aparece por defecto en el wizard" />
            <Input label="Orden" type="number" min={0} value={orden}
              onChange={(e) => setOrden(e.target.value)} wrapperClassName="max-w-[120px]" />
          </div>

          {reactivable && (
            <div className="flex items-start gap-2 rounded-v25-md border border-v25-received-bd bg-v25-received-bg p-3 text-xs text-v25-received-fg">
              <RotateCcw className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Ya existe un recurso <strong>«{reactivable.nombre}»</strong> desactivado. En vez de crear otro,
                puedes reactivarlo con los valores de este formulario.
              </span>
            </div>
          )}
        </div>

        {/* ── Derecha · lo que depende del tipo ── */}
        <div className="space-y-4">
          {!esCredencial ? (
            <div className="rounded-v25-md border border-v25-line p-3">
              <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-v25-ink-500">
                <Boxes className="h-3.5 w-3.5" />Inventario
              </div>
              <Switch checked={inventariable} onChange={toggleInventariable} disabled={guardando} label="Es inventariable" />
              <p className="mt-1 text-[11px] text-v25-ink-400">
                Si el recurso se controla en Inventario, márcalo y elige su tipo de activo. Inventario
                tiene un tipo para personal interno y otro para colaboradoras: es el MISMO recurso, con
                una sola área, así que si se entrega a los dos, elige los dos.
              </p>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {[
                  ['interno', tipoId, setTipoId],
                  ['colab', tipoColabId, setTipoColabId],
                ].map(([scope, valor, poner]) => (
                  <Select key={scope} label={`Tipo en Inventario · ${ambitoFrase(scope)}`} value={valor}
                    onChange={(e) => { poner(e.target.value); setErrores((er) => ({ ...er, tipo: undefined })); }}
                    disabled={!inventariable || guardando} error={scope === 'interno' ? errores.tipo : undefined}
                    hint={!inventariable ? 'Solo para recursos inventariables.'
                      // Sin el de un ámbito, la baja de esas personas no sabrá de qué área es su material.
                      : !valor ? `Sin él, el material de ${ambitoFrase(scope)} no tendrá área en las bajas.` : undefined}>
                    <option value="">{inventariable ? 'Ninguno' : '—'}</option>
                    {tiposDe[scope].map((x) => <option key={x.id} value={x.id}>{x.nombre}</option>)}
                  </Select>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-3 rounded-v25-md border border-v25-received-bd bg-v25-received-bg/40 p-3">
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-v25-ink-500">
                  <KeyRound className="h-3.5 w-3.5" />Credencial
                </div>

                <Select label="Campo de identidad que rellena" value={campoPersona}
                  onChange={(e) => { setCampoPersona(e.target.value); setErrores((er) => ({ ...er, campoPersona: undefined })); }}
                  disabled={guardando} error={errores.campoPersona}
                  hint="La mayoría de credenciales no rellenan ninguna: el CRM, la intranet o la plataforma de fincas no escriben nada en la ficha. Solo elige una columna si esta credencial ES ese dato de la persona.">
                  <option value="">— Ninguna · no rellena ningún campo de la persona —</option>
                  {CAMPOS_PERSONA.map((c) => (
                    <option key={c.v} value={c.v} disabled={ocupadas.campo_persona.has(c.v)}>
                      {c.label}{ocupadas.campo_persona.has(c.v) ? ` · ya lo usa «${ocupadas.campo_persona.get(c.v)}»` : ''}
                    </option>
                  ))}
                </Select>

                <Select label="Dónde vive su contraseña" value={campoPassword}
                  onChange={(e) => { setCampoPassword(e.target.value); setErrores((er) => ({ ...er, campoPassword: undefined })); }}
                  disabled={guardando} error={errores.campoPassword}
                  hint="Solo las cinco columnas de la pestaña Soporte. Una credencial nueva no tiene dónde: su contraseña se envía y no se guarda.">
                  <option value="">— No se guarda (va por Vaultwarden) —</option>
                  {CAMPOS_PASSWORD.map((c) => (
                    <option key={c.v} value={c.v} disabled={ocupadas.campo_password.has(c.v)}>
                      {c.label}{ocupadas.campo_password.has(c.v) ? ` · ya lo usa «${ocupadas.campo_password.get(c.v)}»` : ''}
                    </option>
                  ))}
                </Select>

                {/* ── EL AVISO QUE IMPORTA (§4.8) ──────────────────────────────────────────
                    Las bajas deducen qué accesos tiene una persona de las COLUMNAS de su ficha,
                    porque `persona_credenciales` sólo tiene filas de quien pasó por el módulo.
                    Una credencial sin ninguna columna no se puede detectar: no hay nada que leer.

                    Se dice AQUÍ y no en el panel de la baja porque aquí todavía se puede elegir
                    otra cosa. Decírselo después, cuando alguien esté dando de baja a una persona,
                    llega tarde — y para entonces puede haber cinco credenciales indetectables. */}
                {!campoPersona && !campoPassword && (
                  <div className="flex items-start gap-2 rounded-v25-md border border-amber-200 bg-amber-50 px-3 py-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                    <div className="min-w-0 text-[11px] leading-relaxed text-amber-800">
                      <strong className="font-semibold text-amber-900">Las bajas no podrán detectar este acceso.</strong>{' '}
                      Sin columna de identidad ni de contraseña no hay nada que leer en la ficha, así
                      que para quien no haya pasado por este módulo será invisible: habrá que
                      registrarlo a mano o añadirlo como tarea en cada baja.
                    </div>
                  </div>
                )}

                {/* Y la señal débil: deducir por contraseña alcanza a poca gente. */}
                {!campoPersona && campoPassword && (
                  <div className="flex items-start gap-2 rounded-v25-md border border-v25-line bg-v25-surface-sunken px-3 py-2">
                    <Info className="mt-0.5 h-4 w-4 shrink-0 text-v25-ink-400" />
                    <div className="min-w-0 text-[11px] leading-relaxed text-v25-ink-500">
                      Las bajas lo deducirán <strong className="font-medium">sólo de la contraseña</strong>, y
                      es una señal débil: las contraseñas viven en Vaultwarden y estas columnas están
                      rellenas en muy poca gente. Si este acceso tiene un usuario que sí se guarda en
                      la ficha, elígelo arriba como campo de identidad.
                    </div>
                  </div>
                )}

                {campoPassword && (
                  <p className="flex items-start gap-1.5 text-[11px] text-v25-ink-500">
                    <Lock className="mt-0.5 h-3 w-3 shrink-0" />
                    El panel del alta mostrará un enlace con candado a la pestaña Soporte. La contraseña no se enseña ahí.
                  </p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-v25-ink-700">Instrucciones de acceso</label>
                <EditorInstrucciones value={instrucciones} onChange={setInstrucciones} minHeight={280} />
                <p className="mt-1 text-[11px] text-v25-ink-400">
                  Cómo se entra al CRM no es cómo se entra a Microsoft 365. Es el cuerpo del correo del acceso.
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Barra fija: con el editor la página crece y el botón de guardar se iría fuera de vista. */}
      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-v25-line bg-white/95 px-6 py-3 backdrop-blur">
        <div className="flex items-center gap-2">
          {hayCambios && (
            <span className="flex items-center gap-1.5 text-[11px] text-v25-ink-500">
              <AlertTriangle className="h-3.5 w-3.5" />Cambios sin guardar
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" onClick={salir} disabled={guardando}>Cancelar</Button>
            {reactivable ? (
              <Button onClick={reactivarDuplicado} disabled={guardando}>
                {guardando ? <Loader2 className="animate-spin" /> : <RotateCcw />}Reactivar el recurso existente
              </Button>
            ) : (
              <Button onClick={guardar} disabled={guardando}>
                {guardando ? <Loader2 className="animate-spin" /> : null}{esEdicion ? 'Guardar cambios' : 'Crear recurso'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
